import React, { useState } from 'react';
import { Layout } from '../components/Layout';
import { FileBrowser } from '../components/FileBrowser';
import { FolderCreationModal } from '../components/FolderCreationModal';
import { FileUpload } from '../components/FileUpload';
import { FileDownload } from '../components/FileDownload';
import { SignedLinkGenerator } from '../components/SignedLinkGenerator';
import { FileDeletion } from '../components/FileDeletion';
import { FileMetadata, FolderStructure } from '../../../shared/types';
import { Download, Share, Plus, Upload, X, AlertCircle, CheckCircle } from 'lucide-react';

export const DashboardPage: React.FC = () => {
  const [selectedFiles, setSelectedFiles] = useState<FileMetadata[]>([]);
  const [selectedFolders, setSelectedFolders] = useState<FolderStructure[]>([]);
  const [isFolderModalOpen, setIsFolderModalOpen] = useState(false);
  const [isUploadPanelOpen, setIsUploadPanelOpen] = useState(false);
  const [selectedUploadFolder, setSelectedUploadFolder] = useState<FolderStructure | undefined>();
  const [downloadMessage, setDownloadMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [deletionMessage, setDeletionMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleFileSelect = (files: FileMetadata[]) => {
    setSelectedFiles(files);
  };

  const handleFolderSelect = (folders: FolderStructure[]) => {
    setSelectedFolders(folders);
    
    // If a single upload tier folder is selected, set it as the upload target
    if (folders.length === 1 && folders[0].folderPath.startsWith('/upload')) {
      setSelectedUploadFolder(folders[0]);
    } else {
      setSelectedUploadFolder(undefined);
    }
  };

  const handleOpenFolderModal = () => {
    setIsFolderModalOpen(true);
  };

  const handleCloseFolderModal = () => {
    setIsFolderModalOpen(false);
  };

  const handleFolderCreated = () => {
    // Optional: Show success message or perform additional actions
    console.log('Folder created successfully');
  };

  const handleOpenUploadPanel = () => {
    setIsUploadPanelOpen(true);
  };

  const handleCloseUploadPanel = () => {
    setIsUploadPanelOpen(false);
  };

  const handleUploadComplete = () => {
    // Optional: Show success message or perform additional actions
    console.log('Upload completed successfully');
    // Keep the panel open so user can upload more files if needed
  };

  const handleDownloadStart = () => {
    setDownloadMessage(null);
  };

  const handleDownloadComplete = () => {
    setDownloadMessage({ type: 'success', text: 'Download started successfully!' });
    // Clear message after 5 seconds
    setTimeout(() => setDownloadMessage(null), 5000);
  };

  const handleDownloadError = (error: string) => {
    setDownloadMessage({ type: 'error', text: error });
    // Clear message after 10 seconds for errors
    setTimeout(() => setDownloadMessage(null), 10000);
  };

  const handleDeletionComplete = () => {
    setDeletionMessage({ type: 'success', text: 'Selected items deleted successfully!' });
    // Clear selection after successful deletion
    setSelectedFiles([]);
    setSelectedFolders([]);
    // Clear message after 5 seconds
    setTimeout(() => setDeletionMessage(null), 5000);
  };

  const handleDeletionError = (error: string) => {
    setDeletionMessage({ type: 'error', text: error });
    // Clear message after 10 seconds for errors
    setTimeout(() => setDeletionMessage(null), 10000);
  };

  const hasSelection = selectedFiles.length > 0 || selectedFolders.length > 0;

  return (
    <Layout>
      <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-semibold text-gray-900">File Browser</h1>
            <div className="flex items-center space-x-3">
              <button 
                onClick={handleOpenFolderModal}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Folder
              </button>
              <button 
                onClick={handleOpenUploadPanel}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload Files
              </button>
            </div>
          </div>

          {/* Upload Panel */}
          {isUploadPanelOpen && (
            <div className="mt-6 bg-white border border-gray-200 rounded-lg shadow-sm">
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">Upload Files</h2>
                <button
                  onClick={handleCloseUploadPanel}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-4">
                <FileUpload
                  selectedFolder={selectedUploadFolder}
                  onUploadComplete={handleUploadComplete}
                />
                
                {!selectedUploadFolder && (
                  <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-md">
                    <p className="text-sm text-amber-800">
                      <strong>Tip:</strong> Select a folder from the Upload Tier in the file browser below to set it as the upload destination.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Action bar - shown when items are selected */}
          {hasSelection && (
            <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <span className="text-sm font-medium text-blue-900">
                    {selectedFiles.length} files and {selectedFolders.length} folders selected
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <FileDownload
                    selectedFiles={selectedFiles}
                    selectedFolders={selectedFolders}
                    onDownloadStart={handleDownloadStart}
                    onDownloadComplete={handleDownloadComplete}
                    onDownloadError={handleDownloadError}
                  />
                  <SignedLinkGenerator
                    selectedFiles={selectedFiles}
                    selectedFolders={selectedFolders}
                  />
                  <FileDeletion
                    selectedFiles={selectedFiles}
                    selectedFolders={selectedFolders}
                    onDeletionComplete={handleDeletionComplete}
                    onDeletionError={handleDeletionError}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Download Messages */}
          {downloadMessage && (
            <div className={`mt-4 p-4 rounded-md ${
              downloadMessage.type === 'success' 
                ? 'bg-green-50 border border-green-200' 
                : 'bg-red-50 border border-red-200'
            }`}>
              <div className="flex">
                {downloadMessage.type === 'success' ? (
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                )}
                <div className="ml-3">
                  <p className={`text-sm ${
                    downloadMessage.type === 'success' ? 'text-green-800' : 'text-red-800'
                  }`}>
                    {downloadMessage.text}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Deletion Messages */}
          {deletionMessage && (
            <div className={`mt-4 p-4 rounded-md ${
              deletionMessage.type === 'success' 
                ? 'bg-green-50 border border-green-200' 
                : 'bg-red-50 border border-red-200'
            }`}>
              <div className="flex">
                {deletionMessage.type === 'success' ? (
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                )}
                <div className="ml-3">
                  <p className={`text-sm ${
                    deletionMessage.type === 'success' ? 'text-green-800' : 'text-red-800'
                  }`}>
                    {deletionMessage.text}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* File Browser */}
          <div className="mt-6">
            <FileBrowser
              onFileSelect={handleFileSelect}
              onFolderSelect={handleFolderSelect}
              className="min-h-[600px]"
            />
          </div>

          {/* Quick stats */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-green-100 rounded-md flex items-center justify-center">
                    <Plus className="w-5 h-5 text-green-600" />
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900">Upload Tier</h3>
                  <p className="text-sm text-gray-600">Fast access storage for recent files</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-blue-100 rounded-md flex items-center justify-center">
                    <Download className="w-5 h-5 text-blue-600" />
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900">Archive Tier</h3>
                  <p className="text-sm text-gray-600">Long-term storage for older files</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-purple-100 rounded-md flex items-center justify-center">
                    <Share className="w-5 h-5 text-purple-600" />
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900">Shared Links</h3>
                  <p className="text-sm text-gray-600">Generate secure download links</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Folder Creation Modal */}
      <FolderCreationModal
        isOpen={isFolderModalOpen}
        onClose={handleCloseFolderModal}
        onSuccess={handleFolderCreated}
      />
    </Layout>
  );
};